<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" @show="onShow" @hidden="onHidden" :show-confirmation="false"
                   :title="$t(`Payments for {no}`, {no: invoice.invoice_no})"
                   hide-footer :content-in-form="false" :is-loading="loading" body-class="pt-0">
      <artisio-table v-if="invoice && invoice.invoice_payments" :items="invoice.invoice_payments" :fields="tableFields"
                     :index="false">
        <template slot="cell(type)" slot-scope="{item}">
          {{ item.payment.type | removeUnderscores | toUpperCaseFirst | translate }}
        </template>
        <template slot="cell(method)" slot-scope="{item}">
          {{ item.payment.method | asPaymentMethod }}
        </template>
        <template slot="cell(bank_account)" slot-scope="{item}">
          {{ item.payment.bank_account }}
        </template>
        <template slot="cell(amount)" slot-scope="{item}">
          {{ item.amount | asCurrency(item.payment.currency.symbol) }}
        </template>
        <template slot="cell(date)" slot-scope="{item}">
          {{ item.payment.date | toDate }}
        </template>
        <template slot="cell(description)" slot-scope="{item}">
          {{ item.payment.description }}
        </template>
        <template slot="cell(reference_number)" slot-scope="{item}">
          {{ item.payment.reference_number }}
        </template>
        <template slot="cell(export)" slot-scope="{item}" v-if="isExportFieldExist && modal.source !== SOURCE_BATCH_MARK_UNSOLD_MODAL">
          <a href="javascript:void(0)" @click="onFinancialExportInvoiceClick(item)">
            {{ item.financial_export ? item.financial_export.export_no : '' }}
          </a>
        </template>
        <template slot="cell(reconciled)" slot-scope="{item}">
          <div class="d-flex justify-content-center">
            <b-form-checkbox :checked="item.payment.reconciled" @change="toggleReconciled(item)" v-if="modal.source !== SOURCE_BATCH_MARK_UNSOLD_MODAL"/>
            <b-form-checkbox v-else :checked="item.payment.reconciled" disabled/>
          </div>
        </template>
        <template slot="cell(batch_payment)" slot-scope="{item}">
          <div v-if="item.payment.batch_payment">
            <a href="javascript:void(0)" @click="onBatchInfoClick(item)" class="d-block">
              {{ item.payment.batch_payment.payment_no }}
            </a>
            <span v-if="item.payment.batch_payment.status" v-as-status="item.payment.batch_payment.status">
              {{ item.payment.batch_payment.status | removeUnderscores | toUpperCaseFirst }}
            </span>
          </div>
        </template>
        <template slot="cell(comment)" slot-scope="{item}">
          {{ item.comment ? item.comment : '' }}
        </template>
        <template slot="cell(buyer_group_account)" slot-scope="{item}" v-if="vendorBuyerGroupAccountsEnabled">
          {{ invoice.auction ? invoice.auction.buyer_group_account : '' }}
        </template>
        <template slot="cell(actions)" slot-scope="{item: payment}" v-if="modal.source !== SOURCE_BATCH_MARK_UNSOLD_MODAL">
          <b-tooltip :target="`payment-edit-btn-${payment.uuid}`" :title="$t('Edit Payment')"/>
          <b-button :id="`payment-edit-btn-${payment.uuid}`" v-permission="'change_invoice'"
                    v-if="payment.payment.method !== 'overpayment'" :disabled="payment.financial_export"
                    variant="primary" size="sm" class="mr-2" @click="onPaymentEditClick(payment)">
            <font-awesome-icon icon="pen"></font-awesome-icon>
          </b-button>
          <b-tooltip :target="`payment-delete-btn-${payment.uuid}`" :title="$t('Delete Payment')"/>
          <b-button :id="`payment-delete-btn-${payment.uuid}`" v-permission="'change_invoice'"
                    v-if="payment.payment.method !== 'overpayment'" :disabled="payment.financial_export"
                    variant="danger" size="sm" class="mr-2" @click="onPaymentDeleteClick(payment)">
            <font-awesome-icon icon="trash" class="icon"/>
          </b-button>
          <b-tooltip :target="`payment-undo-btn-${payment.uuid}`" :title="$t('Undo Overpayment Allocation')"/>
          <b-button :id="`payment-undo-btn-${payment.uuid}`" v-permission="'change_invoice'"
                    v-if="payment.payment.method === 'overpayment' && payment.payment.overpayment_source_uuid && !payment.payment.disable_overpayment_undo"
                    variant="danger" size="sm" class="mr-2" @click="onPaymentUndoClick(payment)">
            <font-awesome-icon icon="undo" class="icon"/>
          </b-button>
        </template>
      </artisio-table>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import ArtisioTable from "../../core/components/ams-table/ArtisioTable";
  import InvoicesService from "./InvoicesService";
  import {SOURCE_INVOICES_TABLE, SOURCE_BATCH_MARK_UNSOLD_MODAL} from "../constants";
  import {ON_PDF_MODAL_CLOSE, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_OPEN} from "@/plugins/event-bus/event-bus-names";
  import ExportsService from "@/core/services/exports.service";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapActions: invoicePaymentMapActions} = createNamespacedHelpers('invoicePayments');

  export default {
    name: "PaymentListModal",
    components: {
      ArtisioTable,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        SOURCE_BATCH_MARK_UNSOLD_MODAL: SOURCE_BATCH_MARK_UNSOLD_MODAL,
      }
    },
    computed: {
      ...mapStateGlobal(['vendorBuyerGroupAccountsEnabled', 'paymentBankAccounts']),
      ...mapState({
        modal: state => state.paymentListModal,
        invoice: state => state.paymentListModal.invoice,
        isExportFieldExist() {
          return this.$hasPermission('enable_accounting_export')
        }
      }),
      tableFields() {
        let tableFields = [
          {key: "type", label: this.$i18n.t('Type')},
          {key: "method", label: this.$i18n.t('Method')},
          {key: "amount", label: this.$i18n.t('Amount')},
          {key: "date", label: this.$i18n.t('Date')},
          {key: "bank_account", label: this.$i18n.t('Bank Account')},
          {key: "description", label: this.$i18n.t('Description')},
          {key: "reference_number", label: this.$i18n.t('Reference No.')},
          {key: "export", label: this.$i18n.t('Export')},
          {key: "reconciled", label: this.$i18n.t('Reconciled')},
          {key: "comment", label: this.$i18n.t('Comment')},
          {key: "buyer_group_account", label: this.$i18n.t('Buyer Group Account')},
          {key: "batch_payment", label: this.$i18n.t('Batch ID')},
          {key: "actions", label: this.$i18n.t('Actions')},
        ];

        if (this.invoice.status === 'cancelled') {
          tableFields = tableFields.filter(f => f.key !== 'actions')
        }

        if (!this.isExportFieldExist) {
          tableFields = tableFields.filter(f => f.key !== 'export')
        }

        if (this.modal.source === this.SOURCE_BATCH_MARK_UNSOLD_MODAL) {
          tableFields = tableFields.filter(f => !['export', 'actions'].includes(f.key));
        }

        if (this.paymentBankAccounts.length === 0) {
          tableFields = tableFields.filter(f => f.key !== 'bank_account')
        }

        if (!this.vendorBuyerGroupAccountsEnabled) {
          tableFields = tableFields.filter(f => f.key !== 'buyer_group_account')
        }

        return tableFields;
      }
    },
    methods: {
      ...mapActions(['hidePaymentListModal', 'showPaymentModal', 'getTableData',
        'updatePaymentListModalInvoice', 'updatePaymentModalObject']),
      ...invoicePaymentMapActions({
        invoicePaymentsUpdateTableSource: 'updateTableSource',
        invoicePaymentsUpdateSearchModel: 'updateSearchModel',
      }),
      onShow() {
        this.showConfirmation = true
      },
      onHidden() {
        this.hidePaymentListModal();
      },
      async onPaymentEditClick(invoicePayment) {
        await InvoicesService.onEditPayment(this, invoicePayment, this.modal.source)
      },
      async onFinancialExportInvoiceClick(item) {
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t(`Financial Export ${item.financial_export.export_no}`),
        });
        const {success, body} = await ExportsService.exportFinancialHistory({
          uuid: item.financial_export.uuid
        })

        if (success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            pdf_url: body.url,
          })
        } else {
          this.$evBus.$emit(ON_PDF_MODAL_CLOSE);

          let errorMessage = "";
          if (body.non_field_errors) {
            errorMessage = body.non_field_errors[0];
          } else {
            errorMessage = body;
          }
          this.$errorDialog(errorMessage);
        }
      },
      async onPaymentDeleteClick(invoicePayment) {
        await InvoicesService.onDeletePayment(this, invoicePayment, this.modal.source)
      },
      async toggleReconciled(invoice_payment) {
        const {payment: {uuid, reconciled}} = invoice_payment;
        const response = await InvoicesService.partialUpdatePayment(uuid, {
          reconciled: !reconciled
        });

        if (response && response.success) {
          await this.getTableData();
          this.updatePaymentListModalInvoice();
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0]);
        }
      },
      async onPaymentUndoClick(invoicePayment) {
        await InvoicesService.onOverpaymentUndo(this, invoicePayment, this.modal.source)
      },
      async onBatchInfoClick(invoicePayment) {
        await this.invoicePaymentsUpdateTableSource({source: 'finance'});
        await this.invoicePaymentsUpdateSearchModel({searchModel: {batch_id: invoicePayment.payment.batch_payment.payment_no}});
        this.hidePaymentListModal();
        this.$router.push({name: 'invoicesPayments'})
      }
    }
  }
</script>

<style scoped>

</style>
